// Einstellungen des Assistenten je Betrieb: Opt-in, Aufbewahrung, Monatsbudget – und der Hinweis, was das System
// verlässt (ki-assistent §11). Im Auslieferungszustand ist der Assistent AUS.
import { z } from 'zod'
import { PERMISSION_AUTHENTICATED, defineContract } from '@shared/contract'

export const ASSISTANT_SETTINGS_PERMISSION = 'assistant.settings.manage'

export const zAssistantSettings = z.object({
  /** Opt-in des Betriebs */
  enabled: z.boolean(),
  /** wann und von wem der Hinweis bestätigt wurde */
  consentAt: z.string().nullable(),
  consentBy: z.string().nullable(),
  consentVersion: z.string().nullable(),
  /** Fassung des Hinweises, die jetzt gilt – weicht sie ab, ist er beim Einschalten erneut zu bestätigen */
  noticeVersion: z.string(),
  /** Unterhaltungen werden so viele Tage nach der letzten Aktivität gelöscht */
  retentionDays: z.int(),
  /** harte Grenze je Kalendermonat in Cent; 0 = der Assistent bleibt aus */
  monthlyBudgetCents: z.int(),
  dailyTurnsPerUser: z.int(),
  suggestionsEnabled: z.boolean(),
  /** „Erweiterte Aktionen“: der Assistent bietet auch Verträge mit assistant: 'opt-in' an – immer mit angehakter Bestätigung */
  advancedActionsEnabled: z.boolean(),
  /** Verbrauch im laufenden Kalendermonat (Europe/Berlin) */
  monthSpentCents: z.int(),
  /** ab 80 % des Budgets */
  budgetWarning: z.boolean(),
  version: z.int(),
})

export const settingsGet = defineContract({
  name: 'assistant.settings.get',
  title: 'Einstellungen des Assistenten anzeigen',
  description: 'Opt-in, Aufbewahrung, Monatsbudget und Verbrauch des laufenden Monats.',
  llm: {
    description: 'Liefert die Einstellungen des Assistenten für den Betrieb. Nur für die Oberfläche.',
    keywords: [],
  },
  input: z.strictObject({}),
  output: zAssistantSettings,
  permission: ASSISTANT_SETTINGS_PERMISSION,
  risk: 'read',
  assistant: 'never',
})

export const settingsUpdate = defineContract({
  name: 'assistant.settings.update',
  title: 'Einstellungen des Assistenten ändern',
  description:
    'Schaltet den Assistenten für den Betrieb ein oder aus und legt Aufbewahrung, Monatsbudget und Tagesgrenze fest. Nur die genannten Werte ändern sich.',
  llm: {
    description:
      'Ändert die Einstellungen des Assistenten. Nur für die Oberfläche – der Assistent richtet sich nicht selbst ein.',
    keywords: [],
  },
  input: z.strictObject({
    enabled: z.boolean().optional(),
    /** Pflicht beim Einschalten: Der Hinweis „Was das System verlässt“ wurde gelesen und bestätigt */
    acceptNotice: z.boolean().optional(),
    retentionDays: z
      .int()
      .min(1, { error: 'Die Aufbewahrung beträgt mindestens einen Tag.' })
      .max(730, { error: 'Die Aufbewahrung beträgt höchstens 730 Tage.' })
      .optional(),
    monthlyBudgetCents: z
      .int()
      .min(0, { error: 'Das Monatsbudget darf nicht negativ sein (Betrag in Cent).' })
      .max(100_000_000, { error: 'Das Monatsbudget ist zu hoch.' })
      .optional(),
    dailyTurnsPerUser: z
      .int()
      .min(1, { error: 'Mindestens eine Anfrage je Tag.' })
      .max(1000, { error: 'Höchstens 1.000 Anfragen je Tag.' })
      .optional(),
    suggestionsEnabled: z.boolean().optional(),
    advancedActionsEnabled: z.boolean().optional(),
    expectedVersion: z.int().optional(),
  }),
  output: zAssistantSettings,
  permission: ASSISTANT_SETTINGS_PERMISSION,
  risk: 'write',
  // Selbsterhöhungs-Sperre: Über den Assistenten hebt niemand dessen Budget an oder schaltet ihn frei
  assistant: 'never',
})

export const noticeGet = defineContract({
  name: 'assistant.notice.get',
  title: 'Datenschutz-Hinweis des Assistenten anzeigen',
  description: 'Was an den KI-Dienst geht, was nie, und wie lange Unterhaltungen aufbewahrt werden.',
  llm: { description: 'Liefert den Datenschutz-Hinweis des Assistenten. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({}),
  output: z.object({
    version: z.string(),
    /** Unterhaltungen: Löschung nach so vielen Tagen ohne Aktivität */
    retentionDays: z.int(),
    sections: z.array(z.object({ title: z.string(), items: z.array(z.string()) })),
  }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
  assistant: 'never',
})
